// FILE: src/components/case-studies/admin/ImportCaseModal.js
//
// Whole-case bundle import (pm-pixel-case-bundle@1, migration 074).
// Flow: pick file → client pre-validation (same rules as Bulk Import for
// embedded questions) → summary → atomic server import → validation report.
// The server rolls the entire case back on any error, so a failed import
// leaves nothing behind.

import React, { useState } from 'react';
import { validateCaseBundle } from './caseAdminUtils';
import { S, T } from './caseAdminStyles';

export default function ImportCaseModal({ onClose, onImported, importFn }) {
  const [bundle, setBundle] = useState(null);
  const [check, setCheck] = useState(null);
  const [mode, setMode] = useState('create');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        setBundle(parsed);
        setCheck(validateCaseBundle(parsed));
      } catch (err) {
        setBundle(null); setCheck(null);
        setError(`Not valid JSON: ${err.message}`);
      }
    };
    reader.readAsText(file);
  }

  async function doImport() {
    setBusy(true); setError(null);
    try {
      const res = await importFn(bundle, mode);
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  const blocked = !bundle || (check && check.errors.length > 0);

  return (
    <div style={S.modalScrim} onClick={() => !busy && onClose()}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()} data-testid="import-case-modal">
        <h2 style={S.h1}>Import case bundle</h2>
        <div style={{ ...S.sub, marginBottom: 10 }}>
          A bundle contains the whole case — overview, stages, sections, artifacts, and questions —
          and imports atomically (all or nothing). Export a case from the editor to see the format.
        </div>

        {!result && (<>
          <input type="file" accept=".json,application/json" data-testid="import-case-file" onChange={onFile} />

          {check && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.84rem', marginBottom: 8 }} data-testid="import-summary">
                <strong>{bundle.case?.title || bundle.case?.slug}</strong> ({(bundle.case?.certification || '').toUpperCase()}) —{' '}
                {check.summary.stages} stage(s), {check.summary.sections} section(s), {check.summary.artifacts} artifact(s), {check.summary.questions} question(s)
              </div>
              {check.errors.map((m, i) => (
                <div key={i} style={{ ...S.err, margin: '4px 0' }} data-testid="import-error">✕ {m}</div>
              ))}
              {check.warnings.map((m, i) => (
                <div key={i} style={{ background: T.warnSoft, color: '#6b4d12', borderRadius: 6, padding: '5px 9px', fontSize: '0.78rem', margin: '4px 0' }}
                  data-testid="import-warning">⚠ {m}</div>
              ))}
              <label style={S.label}>If the slug already exists</label>
              <select style={S.input} data-testid="import-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="create">Fail (create only)</option>
                <option value="replace">Replace draft/review case in place</option>
              </select>
            </div>
          )}
          {error && <div style={S.err} data-testid="import-rpc-error">{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button style={S.btn} disabled={busy} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} data-testid="import-case-submit" disabled={busy || blocked} onClick={doImport}>
              {busy ? 'Importing…' : 'Import case'}
            </button>
          </div>
        </>)}

        {result && (
          <div data-testid="import-result">
            <div style={{ background: T.goodSoft, border: `1px solid ${T.good}`, color: T.good, borderRadius: 8, padding: '9px 12px', fontWeight: 700, margin: '10px 0' }}>
              ✓ Imported "{result.slug}" — {result.stages} stage(s), {result.artifacts} artifact(s),{' '}
              {result.questions_created} question(s) created, {result.questions_linked_existing} linked existing
            </div>
            {result.validation && !result.validation.valid && (
              <div style={{ ...S.sub, marginBottom: 6 }}>
                Publish gate currently reports {result.validation.errors.length} blocking item(s) — open the
                Validation tab to review them (expected for drafts, e.g. missing ECO metadata).
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={S.btnPrimary} data-testid="import-open-case" onClick={() => onImported(result.case_study_id)}>
                Open in editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
